import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { Dialog, DialogContent } from '@mui/material';

import DialogHeader from './DialogHeader';
import useSearchParams from 'hooks/useSearchParams';
import { AccessCodeForm, Hr } from 'components/DeliveryManager';
import { DialogActionButton, DialogFooter } from 'components/Dialog';

import api from 'utils/api';
import { NO_ACCESS_CODE, INVALID_ACCESS_CODE, GATED_ADDRESS, ACCESS_CODE } from 'constants/common';

const PREFIX = 'AccessCodeDialog';

const classes = {
  root: `${PREFIX}-root`,
  title: `${PREFIX}-title`,
  text: `${PREFIX}-text`,
  btn: `${PREFIX}-btn`,
  notice: `${PREFIX}-notice`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`&.${classes.root}`]: {
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.spacing(4),
    },
  },

  [`& .${classes.title}`]: {
    paddingBottom: 0,
  },

  [`& .${classes.text}`]: {
    color: theme.colorTokens.onSurfaceTextTitle,
    fontSize: '14px',
    marginTop: 0,
  },

  [`& .${classes.btn}`]: {
    borderRadius: '2rem',
    marginLeft: '0 !important',
  },

  [`& .${classes.notice}`]: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
  },
}));

const AccessCodeDialog = compose(
  inject('store'),
  observer,
)(({ store }) => {
  const { trackingCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { handleSubmit, control, setValue, formState, watch } = useForm({ defaultValues: { access_code_map: [] }, shouldFocusError: true });
  const { fields, append, remove } = useFieldArray({ control, name: 'access_code_map' });
  const { errors } = formState;

  const watchAccessCodes = watch('access_code_map');
  const controlledAccessCodes = fields.map((field, index) => ({ ...field, ...watchAccessCodes[index] }));

  const reason = searchParams.get('reason');
  const skipQuestions = searchParams.get('skip_questionnaire');

  const { deliveryStore } = store;
  const { delivery } = deliveryStore;
  const accessCodeMap = delivery?.access_code_map;
  const filteredCodes = Object.keys(accessCodeMap || {})
    .map((type) => ({ type, code: accessCodeMap[type] }))
    .filter(({ code }) => Boolean(code));

  const onSubmit = (data) => {
    // no-dd-sa:javascript-code-style/assignment-name
    const access_codes = data.access_code_map;
    // no-dd-sa:javascript-code-style/assignment-name
    const access_code_map = _.merge({}, ...access_codes.map((item) => ({ [item.type]: item.code })));

    const formData = new FormData();
    formData.append('access_code_map', JSON.stringify(access_code_map));
    api.post(`delivery/${trackingCode}/${delivery.shipment.id}/update`, formData, { headers: { 'Content-Type': undefined } }).then((response) => {
      if (response.status !== 200)
        return toast.error(<span className={classes.notice}>Something went wrong. Please try again!</span>, {
          theme: 'colored',
          hideProgressBar: true,
        });
      deliveryStore.getDelivery(trackingCode);
      handleCancel();
    });
  };

  const handleCancel = () => {
    searchParams.set('dialog', 'closed');

    if (!skipQuestions) searchParams.set('skip_questionnaire', 'skippable');
    setSearchParams(searchParams);
  };

  const renderTitle = () => {
    if (reason === NO_ACCESS_CODE) return 'Unsuccessful Delivery - No Access Code';
    if (reason === INVALID_ACCESS_CODE) return 'Unsuccessful Delivery - Wrong Access Code';
    if (reason === GATED_ADDRESS) return 'Gated Address - Codes';
    if (reason === ACCESS_CODE) return 'Codes';

    return '';
  };

  const renderText = () => {
    if (reason === NO_ACCESS_CODE || reason === INVALID_ACCESS_CODE)
      return "We're sorry that we could not deliver your previous shipment. Please add your access code below. You can also cancel and edit your codes later.";
    if (reason === GATED_ADDRESS)
      return _.isEmpty(filteredCodes)
        ? 'Please share the access codes required to deliver to this address. If no codes are required, click Cancel. You can still edit your codes later in our Codes section.'
        : 'Please confirm if provided access codes are correct. In case you click on Cancel, you can still edit your codes later in our Codes section.';
    if (reason === ACCESS_CODE)
      return 'While Jitsu will attempt to complete your delivery change requests, you will not be able to update the scheduled delivery date.';

    return '';
  };

  useEffect(() => {
    if (_.isEmpty(accessCodeMap)) return;

    const codes = Object.keys(accessCodeMap)
      .map((type) => ({ type, code: accessCodeMap[type] }))
      .filter(({ code }) => Boolean(code));
    setValue('access_code_map', codes);
  }, [accessCodeMap, setValue]);

  return (
    <StyledDialog open maxWidth="md" fullWidth className={classes.root}>
      <DialogHeader title={renderTitle()} />
      <DialogContent>
        <p className={classes.text}>{renderText()}</p>
        {reason === ACCESS_CODE && (
          <>
            <Hr />
            <p className={classes.text}>Let us know if there are codes to access your address.</p>
          </>
        )}
        <AccessCodeForm
          fields={fields}
          append={append}
          remove={remove}
          control={control}
          controlledAccessCodes={controlledAccessCodes}
          errors={errors}
        />
      </DialogContent>
      <DialogFooter>
        <DialogActionButton onClick={handleCancel} className={classes.btn} size="medium" color="primary" variant="outlined" disableElevation>
          Cancel
        </DialogActionButton>
        <DialogActionButton
          disabled={!_.isEmpty(errors)}
          onClick={handleSubmit(onSubmit)}
          className={classes.btn}
          size="medium"
          color="primary"
          variant="contained"
          disableElevation
        >
          Save
        </DialogActionButton>
      </DialogFooter>
    </StyledDialog>
  );
});

export default AccessCodeDialog;
